import type { ReactNode } from 'react'
import { useAutosave } from '@/core/hooks/useAutosave'
import { AutosaveStatus } from '@/core/components/form/AutosaveBoundary'
import { ModalHead, ScrollBar } from 'admination-react'
import { useEffect, useRef, useState } from 'react'
import { ApiError } from '@/core/api/http'
import { useApp } from '@/core/state/appStore'
import { currentVariant } from '@/core/data/session'
import { listMassnahmenFull, updateMassnahme, type MassnahmeRow } from '@/core/data/massnahmen'
import { memberDisplayName } from '@/core/lib/orgMembers'
import { FormRenderer } from '@/core/components/form/FormRenderer'
import { initialFormValues, updateFormField, type FormValues } from '@/core/components/form/formModel'
import { descriptionFor } from '@/variants'

import { valuesForMassnahme, patchFromValues, MASSNAHME_SELECT_REQUIRED_FIELDS } from './massnahmeForm'

const ANSPRECHPARTNER_PATH = 'Ansprechpartner.Ansprechpartner'
const STICHZEIT_PATH = 'Anwesenheit.Stichzeit'
const MEMBER_PATHS: ReadonlySet<string> = new Set([ANSPRECHPARTNER_PATH, 'Aktivierung.Zuständig für Aktivierung'])

export function MassnahmeDetails({ massnahmeId, actions }: { massnahmeId: string | null; actions?: ReactNode }) {
  const { state, dispatch, reloadMassnahmen, identity, members } = useApp()
  const scrollerRef = useRef<HTMLElement>(null)

  const { massnahmeForm: massnahmeTemplate, anwesenheit } = descriptionFor(currentVariant())
  const UnterrichtsfreiTage = anwesenheit?.UnterrichtsfreiTage
  const organisationId = identity?.organisationId
  const isOwner = identity?.isOwner ?? false
  const [rows, setRows] = useState<MassnahmeRow[]>([])

  useEffect(() => {
    if (!organisationId) return
    listMassnahmenFull(organisationId)
      .then(setRows)
      .catch((e) => console.error("Maßnahmen laden fehlgeschlagen:", e))
  }, [organisationId, massnahmeId, state.massnahmenVersion])

  const massnahme = rows.find((m) => m.id === massnahmeId) ?? null

  const [values, setValues] = useState<FormValues | null>(null)
  const stichzeitFehlt = isOwner && !!massnahme?.anwesenheit_stichtag && values?.Anwesenheit?.Stichzeit === ''
  const nichtsGesendet = useRef(false)
  const lastMassnahmeId = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    if (!massnahme) return
    if (lastMassnahmeId.current !== massnahmeId) {
      lastMassnahmeId.current = massnahmeId
      const v = initialFormValues(massnahmeTemplate, valuesForMassnahme(massnahme))
      setValues(v)
    }
  }, [massnahme, massnahmeId, massnahmeTemplate])

  const close = () => dispatch({ type: 'CLOSE' })

  const setField = (path: string, v: unknown) => {
    setValues((prev) => updateFormField(prev, path, v))
  }

  const commit = (cur: FormValues | null, baseline: FormValues | null | undefined) => {
    if (!cur || !massnahme || !isOwner) return
    const current = patchFromValues(cur)
    const previous = baseline ? patchFromValues(baseline) : undefined
    const changed = Object.fromEntries(Object.entries(current).filter(([key, value]) => previous?.[key as keyof typeof current] !== value))
    nichtsGesendet.current = !Object.keys(changed).length
    if (nichtsGesendet.current) return
    return updateMassnahme(massnahme.id, changed)
      .then(() => reloadMassnahmen())
      .catch((e) => {
        console.error("Maßnahme speichern fehlgeschlagen:", e)
        if (e instanceof ApiError && e.status === 403 && 'anwesenheit_stichtag' in changed) setField(STICHZEIT_PATH, massnahme.anwesenheit_stichtag ?? '')
        throw e
      })
  }

  const optionsFor = (path: string, templateOptions: string[]) =>
    MEMBER_PATHS.has(path) ? members.map((m) => m.userId) : templateOptions

  const optionLabel = (path: string, option: string) => {
    if (!MEMBER_PATHS.has(path)) return option
    const m = members.find((x) => x.userId === option)
    return m ? memberDisplayName(m) : option
  }

  const { status } = useAutosave(values, !!values && !!massnahme && isOwner, commit, 250, { form: 'massnahme-details', record: massnahmeId ?? undefined, recover: setValues, label: massnahme?.bezeichnung ?? "Maßnahme" })

  return (
    <>
      <ModalHead titleIcon="i-massnahme" title={massnahme ? massnahme.bezeichnung : "Maßnahme"} actions={<><AutosaveStatus state={status === 'saved' && stichzeitFehlt && nichtsGesendet.current ? 'idle' : status} />{actions}</>} onClose={close} closeLabel="Schließen" />
      <main
        className="form" data-scroll-host ref={scrollerRef}

        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLElement).blur()
        }}
      >
        {values && (
          <FormRenderer
              definition={massnahmeTemplate}
              values={values}
              onChange={setField}
              readOnly={!isOwner}
              field={(field) => ({
                required: field.id === STICHZEIT_PATH || (field.control === 'select' && MASSNAHME_SELECT_REQUIRED_FIELDS?.has(field.id)),
                errorMessage: field.id === STICHZEIT_PATH && stichzeitFehlt ? "Bitte geben Sie eine Stichzeit ein." : undefined,
                options: field.control === 'select' ? optionsFor(field.id, field.options ?? []) : undefined,
                optionLabel: field.control === 'select' ? (option: string) => optionLabel(field.id, option) : undefined,
              })}
            />
        )}
        {values && massnahmeId && UnterrichtsfreiTage && <UnterrichtsfreiTage massnahmeId={massnahmeId} isOwner={isOwner} />}
      </main>
      <ScrollBar scrollerRef={scrollerRef} />
    </>
  )
}
